import styled, { css, keyframes } from 'styled-components';
import type { ContentCardProps, ImageAspectRatio } from '../types';
import Image from 'components/generic/Image';
import { AspectRatioContainer } from 'components/generic/Image/Image';
import type { ImageProps, WithScalingProps } from 'components/generic/Image/shared/types';
import TagList from './TagList';
import VideoPlayIcon from './VideoPlayIcon';
import { useEffect, useState } from 'react';

const fadeImage = keyframes`
    0% {
        opacity: 0;
    }
    75% {
        opacity: 0;
    }
    80% {
        opacity: 1;
    }
    100% {
        opacity: 1;
    }
`;

const ImageWrapper = styled.div<{ $aspectRatio: ImageAspectRatio; $isTransparent?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    object-fit: cover;
    
    ${({ $isTransparent, theme }) =>
        $isTransparent &&
        css`
        border-radius: ${theme.RadiusMedium};
    `};

    ${({ $aspectRatio }) =>
        $aspectRatio === 'expand' &&
        css`
            flex: 0 1 100%;
        `}

    ${({ $aspectRatio }) =>
        $aspectRatio === 'fill' &&
        css`
            flex: 1 0 0%;
        `}
`;

const CrossFadeContainer = styled.div<{ $crossFadeDelay: number }>`
    display: grid;
    grid-template-areas: 'image';
    grid-template-columns: 1fr;
    width: 100%;

    div,
    img {
        grid-area: image;
    }

    ${({ $crossFadeDelay }) =>
        $crossFadeDelay &&
        css`
            & > *:nth-child(2) {
                opacity: 0;

                animation-name: ${fadeImage};
                animation-timing-function: ease-in-out;
                animation-iteration-count: 10;
                animation-duration: 6s;
                animation-direction: alternate;
                animation-delay: ${$crossFadeDelay}s;
            }
        `}
`;

const commonImageStyling = css<{ $fill: boolean }>`
    height: 100%;
    width: 100%;
    object-fit: cover;

    ${({ $fill }) =>
        $fill &&
        css`
            position: absolute;
    `}
`;

const StyledImage = styled(Image)<{ $fill: boolean }>`
    ${commonImageStyling}
`;

const StyledImageWithScaling = styled(Image.WithScaling)<{ $fill: boolean }>`
    ${commonImageStyling}
`;

const StyledAspectRatioContainer = styled(AspectRatioContainer)`
    width: 100%;
`;

const StyledVideoPlayIcon = styled(VideoPlayIcon)`
    position: absolute;
`;

const ImagePlaceholder = styled.div<{ $aspectRatio: ImageAspectRatio }>`
    width: 100%;
    aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
`;

const SplashSlot = styled.div`
    position: absolute;
    top: ${({ theme }) => theme.Spacing8};
    right: ${({ theme }) => theme.Spacing8};
`;

function ImageRenderer({ image }: { image: NonNullable<ContentCardProps['image']> }) {
    const { aspectRatio = '4/3' } = image;
    const isWithScalingProps = (props: ImageProps | WithScalingProps): props is WithScalingProps =>
        (props as WithScalingProps).transformType !== undefined;
    const imageWithFormattedAspectRatio = {
        ...image,
        aspectRatio: aspectRatio === 'expand' || aspectRatio === 'fill' ? undefined : aspectRatio
    };
    const isFill = aspectRatio === 'fill';

    return (
        <>
            {isWithScalingProps(imageWithFormattedAspectRatio) ? (
                <StyledImageWithScaling
                    {...{ ...imageWithFormattedAspectRatio, sizes: imageWithFormattedAspectRatio.sizes || 250 }}
                    AspectRatioContainerRenderer={(props) => <StyledAspectRatioContainer {...props} />}
                    $fill={isFill}
                />
            ) : (
                <StyledImage
                    {...{ ...imageWithFormattedAspectRatio, sizes: imageWithFormattedAspectRatio.sizes || '250px' }}
                    AspectRatioContainerRenderer={(props) => <StyledAspectRatioContainer {...props} />}
                    $fill={isFill}
                />
            )}
        </>
    );
}

function ImageContent({
    image,
    imageSecondary,
    aspectRatio
}: {
    image?: ContentCardProps['image'];
    imageSecondary?: ContentCardProps['imageSecondary'];
    aspectRatio: ImageAspectRatio;
}) {
    const [crossFadeDelay, setCrossFadeDelay] = useState(10);

    useEffect(() => {
        // Random delay for crossfade effect between 1 and 10 seconds
        setCrossFadeDelay(+(Math.random() * 9 + 1).toFixed(1));
    }, []);

    if (!image?.src) {
        return <ImagePlaceholder $aspectRatio={aspectRatio} />;
    }
    if (!imageSecondary) {
        return <ImageRenderer image={{ ...image, aspectRatio }} />;
    }
    return (
        <CrossFadeContainer $crossFadeDelay={crossFadeDelay}>
            <ImageRenderer image={{ ...image, aspectRatio }} />
            <ImageRenderer image={{ ...imageSecondary, aspectRatio }} />
        </CrossFadeContainer>
    );
}

function ImageComponent({
    image,
    imageSecondary,
    tags,
    splashText,
    horizontal = false,
    showPlayIcon,
    isTransparent,
    splashRenderer,
    ...restProps
}: Pick<ContentCardProps, 'image' | 'imageSecondary' | 'tags' | 'splashText' | 'isTransparent' | 'splashRenderer'> & {
    horizontal?: boolean;
    showPlayIcon?: boolean;
}) {
    const { aspectRatio = '4/3' } = image || {};

    return (
        <ImageWrapper $aspectRatio={aspectRatio} $isTransparent={isTransparent} {...restProps}>
            <ImageContent image={image} imageSecondary={imageSecondary} aspectRatio={aspectRatio} />
            {showPlayIcon && <StyledVideoPlayIcon small={horizontal} />}
            <TagList tags={tags} />
            {splashRenderer && <SplashSlot>{splashRenderer()}</SplashSlot>}
        </ImageWrapper>
    );
}

export default ImageComponent;
